import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { faCameraRetro } from '@fortawesome/pro-solid-svg-icons';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, switchMap } from 'rxjs';
import { take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AuthService } from '../../../../../auth/auth.service';
import { UserOperation } from '../../../../../core/enums/user-operation';
import { FileModel } from '../../../../../core/models/file/file.model';
import { ShiveDialogService } from '../../../../../core/services/controls/shive-dialog.service';
import { FileService } from '../../../../../core/services/file.service';
import * as fromFilesActions from '../../../../../core/state/files/files.actions';
import { FileUploadDialogComponent } from '../../../../../shared/components/dialogs/file-upload-dialog/file-upload-dialog.component';
import { DropdownOption } from '../../../../../shared/data-types/dropdown-option';
import { FileEntityKey, FileEntityKind } from '../../../../../shared/data-types/file-types';
import { FormControlNameRecord } from '../../../../../shared/data-types/form-types';
import { HttpParamObject } from '../../../../../shared/data-types/http-types';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'app-user-profile-personal-data',
  templateUrl: './user-profile-personal-data.component.html',
  styleUrl: './user-profile-personal-data.component.scss',
  standalone: false,
})
export class UserProfilePersonalDataComponent implements OnInit, OnDestroy {
  public personalDataForm: FormGroup;
  public formControlNames: FormControlNameRecord = {
    Gender: 'gender',
    Title: 'title',
    FirstName: 'firstName',
    LastName: 'lastName',
    Email: 'email',
    Telephone: 'telephone',
    Birthday: 'birthday',
    Role: 'role',
  };
  public readonly genderTypes: DropdownOption[] = [
    { text: 'Herr', value: 0 },
    { text: 'Frau', value: 1 },
    { text: 'Divers', value: 2 },
    { text: 'Keine Angabe', value: 3 },
  ];
  public readonly roles: DropdownOption[] = [
    { text: 'Administrator', value: 0 },
    { text: 'Projektleiter', value: 1 },
    { text: 'Projektmitglied', value: 2 },
  ];
  public avatarUrl = signal<string | null>(null);
  public initials = signal<string | null>(null);
  public readonly faCirclePlus = faCirclePlus;
  public readonly UserOperation = UserOperation;
  public readonly faCameraRetro = faCameraRetro;

  // DI
  private readonly formBuilder = inject(FormBuilder);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly actions$ = inject(Actions);
  private readonly fileService = inject(FileService);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);

  // Misc
  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.personalDataForm = this.formBuilder.group({
      gender: [],
      title: [],
      firstName: [],
      lastName: [],
      email: [],
      telephone: [],
      birthday: [new Date()],
      role: [],
    });

    this.loadAvatar();
    this.initEvents();
  }

  ngOnDestroy(): void {
    if (this.avatarUrl()) {
      URL.revokeObjectURL(this.avatarUrl());
    }

    this.subs.unsubscribe();
  }

  public changePicture(): void {
    const loggedInUser = this.authService.getLoggedInUserClone();

    const ref = this.shiveDialogService.open({
      title: 'Profilbild ändern',
      content: FileUploadDialogComponent,
      width: 572,
    });
    ref.content.setInput('fileEntityKind', FileEntityKind.Avatar);
    ref.content.setInput('fileEntityKey', FileEntityKey.UserId);
    ref.content.setInput('fileEntityValue', loggedInUser.id);
  }

  private initEvents(): void {
    this.subs.sink = this.actions$
      .pipe(
        ofType(fromFilesActions.fileUploaded),
        switchMap(() => {
          return this.fetchFullSizedAvatar$();
        }),
      )
      .subscribe((newAvatar: FileModel) => {
        if (newAvatar) {
          this.avatarUrl.set(URL.createObjectURL(newAvatar.file));
          this.initials.set(null);
          return;
        }

        const initials = this.userService.getUserInitials(this.authService.getLoggedInUserClone());
        this.initials.set(initials);
      });
  }

  private loadAvatar(): void {
    this.fetchFullSizedAvatar$()
      .pipe(take(1))
      .subscribe((avatar) => {
        if (avatar?.file.name) {
          this.avatarUrl.set(URL.createObjectURL(avatar.file));
          this.initials.set(null);
          return;
        }

        // No avatar available, so show initials.
        const initials = this.userService.getUserInitials(this.authService.getLoggedInUserClone());
        this.initials.set(initials);
      });
  }

  private fetchFullSizedAvatar$(): Observable<FileModel> {
    const loggedInUser = this.authService.getLoggedInUserClone();
    const params: HttpParamObject = {
      kind: FileEntityKind.Avatar,
      [FileEntityKey.FileId]: loggedInUser.id,
    };
    return this.fileService.fetchFile(params, false);
  }
}
